import { useState } from 'react';
import { useAddresses, useCreateAddress, useUpdateAddress, useDeleteAddress } from '../hooks/useAddresses';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createAddressSchema, type Address, type CreateAddressInput } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

export function AddressBookPage() {
  const { data: addresses, isLoading } = useAddresses();
  const createMutation = useCreateAddress();
  const updateMutation = useUpdateAddress();
  const deleteMutation = useDeleteAddress();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Address | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateAddressInput>({
    resolver: zodResolver(createAddressSchema) as never,
  });

  function openNew() {
    setEditing(null);
    reset({ label: '', name: '', name2: '', address1: '', address2: '', city: '', state: '', zip: '', country: 'US' });
    setModalOpen(true);
  }

  function openEdit(row: Address) {
    setEditing(row);
    reset({
      label: row.label, name: row.name, name2: row.name2 ?? '',
      address1: row.address1, address2: row.address2 ?? '',
      city: row.city, state: row.state, zip: row.zip, country: row.country ?? 'US',
    });
    setModalOpen(true);
  }

  async function onSubmit(data: CreateAddressInput) {
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...data });
        toast.success('Address updated');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Address created');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-silver-500">{addresses?.length ?? 0} addresses</p>
        <Button onClick={openNew}><PlusIcon className="h-4 w-4" /> Add Address</Button>
      </div>

      <Card padding={false}>
        <Table<Address>
          loading={isLoading}
          data={addresses ?? []}
          onRowClick={openEdit}
          columns={[
            { key: 'label', header: 'Label' },
            { key: 'name', header: 'Name' },
            { key: 'address1', header: 'Address' },
            { key: 'city', header: 'City' },
            { key: 'state', header: 'State' },
            { key: 'zip', header: 'ZIP' },
            {
              key: 'actions', header: '',
              render: (r) => (
                <Button variant="ghost" size="sm" onClick={(e) => {
                  e.stopPropagation();
                  if (confirm('Delete this address?')) deleteMutation.mutate(r.id);
                }}>
                  Delete
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Address' : 'New Address'} size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Label" placeholder="e.g. Main Office" {...register('label')} error={errors.label?.message} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Name" {...register('name')} error={errors.name?.message} />
            <Input label="Name 2" {...register('name2')} />
          </div>
          <Input label="Address 1" {...register('address1')} error={errors.address1?.message} />
          <Input label="Address 2" {...register('address2')} />
          <div className="grid grid-cols-3 gap-4">
            <Input label="City" {...register('city')} error={errors.city?.message} />
            <Input label="State" {...register('state')} error={errors.state?.message} />
            <Input label="ZIP" {...register('zip')} error={errors.zip?.message} />
          </div>
          <Input label="Country" {...register('country')} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
